import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity, resetEntity } from '@redux/entities'

import api from '@functions/api'

import { handleError } from '../utils'

const FETCH_REQUEST = 'contenthouse/userV4/FETCH_REQUEST'
const FETCH_SUCCESS = 'contenthouse/userV4/FETCH_SUCCESS'
const FETCH_FAILURE = 'contenthouse/userV4/FETCH_FAILURE'

const CREATE_REQUEST = 'contenthouse/userV4/CREATE_REQUEST'
const CREATE_SUCCESS = 'contenthouse/userV4/CREATE_SUCCESS'
const CREATE_FAILURE = 'contenthouse/userV4/CREATE_FAILURE'

const UPDATE_REQUEST = 'contenthouse/userV4/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'contenthouse/userV4/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'contenthouse/userV4/UPDATE_FAILURE'

const DELETE_REQUEST = 'contenthouse/userV4/DELETE_REQUEST'
const DELETE_SUCCESS = 'contenthouse/userV4/DELETE_SUCCESS'
const DELETE_FAILURE = 'contenthouse/userV4/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loading: false,
  result: {},
  updating: false,
}

// Actions
export function fetchRequest(){
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    payload,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadUsersV4(options){
  const { loadNext, ...apiOptions } = options

  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/v4/users', apiOptions)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.USERV4_ARRAY)

        if (loadNext) {
          dispatch(updateEntities(normalizedJson))
        } else {
          dispatch(resetEntity({ entities: { usersV4: normalizedJson.entities.usersV4 || {} } }))
        }

        dispatch(fetchSuccess({ data, hasMore: data.length === apiOptions.size }))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createUserV4(user, options = {}){
  const config = {
    method: 'POST',
    data: user,
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/v4/users', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.USERV4)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateUserV4(user, options = {}){
  const config = {
    method: 'PUT',
    data: user,
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/v4/users/${user.id}`, options, config)
      .then(({ data }) => {
        const updatedData = data
        if (!updatedData.flags){
          updatedData.flags = null
        }

        const normalizedJson = normalize(updatedData, Schemas.USERV4)

        const updateEntityOptions = {
          mergeOptions: {
            customMerge: (key) => {
              if (['editor', 'provider', 'qc'].includes(key)){
                return (_, source) => source
              }

              return undefined
            },
          },
        }

        dispatch(updateEntities(normalizedJson, updateEntityOptions))
        dispatch(updateSuccess())
        return { success: true, updatedData }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteUserV4(user){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/v4/users/${user.id}`, { permanent: true }, config)
      .then(({ data }) => {
        const normalizedJson = normalize(user, Schemas.USERV4)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, result: errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  const { type, payload } = action

  switch (type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        result: { ...state.result, ...payload },
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
